import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ServiceStatusBadge, PriceOrDiscountBlock, type ServiceStatusBadgeTone } from '@/shared/components/cse'
import { formatMoneyCents } from '@/shared/lib/formatMoney'
import { createLazyThemedIcon } from '@/shared/theme/resolveThemedIcon'
import type { Listing, ListingStatus } from '@/features/listing/types'
import { HorizonMediaCard } from '../../../shared/components/HorizonMediaCard'

const ListIcon = createLazyThemedIcon('list')

function statusTone(status: ListingStatus): ServiceStatusBadgeTone {
  switch (status) {
    case 'active':
      return 'open'
    case 'cancelled':
    case 'sold':
    case 'expired':
      return 'closed'
    case 'pending':
    default:
      return 'pending'
  }
}

export function HorizonListingCard({
  listing,
  priority = false,
  footer,
  showStatus = false,
}: {
  listing: Listing
  priority?: boolean
  footer?: ReactNode
  showStatus?: boolean
}) {
  const { t } = useTranslation()
  const photo = listing.photos[0]
  const detailTo = `/listings/${encodeURIComponent(listing.id)}`
  const priceLabel =
    listing.price_cents !== null ? formatMoneyCents(listing.price_cents) : t(`listing.category.${listing.category}`)

  return (
    <HorizonMediaCard
      to={detailTo}
      title={listing.title}
      subtitle={t(`listing.category.${listing.category}`)}
      badges={
        showStatus ? (
          <ServiceStatusBadge label={t(`listing.status.${listing.status}`)} tone={statusTone(listing.status)} />
        ) : (
          <ServiceStatusBadge label={t('listing.status.active')} tone="open" />
        )
      }
      details={<p className="line-clamp-3 m-0">{listing.description}</p>}
      price={<PriceOrDiscountBlock bare value={priceLabel} />}
      actionLabel={t('listing.view')}
      actionAriaLabel={t('listing.view_aria', { title: listing.title })}
      imageUrl={photo?.thumb_url ?? photo?.url}
      imageWebpUrl={photo?.webp_url}
      fallbackIcon={ListIcon}
      priority={priority}
      footer={footer}
    />
  )
}
